import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { Files, Pen, Printer, Trash } from 'react-bootstrap-icons';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import FormGenerator from '../../form-generator/FormGenerator';
import { deleteFileFromServer, makeApiRequests } from '../../helpers/api';
import {
  ENDPOINTS,
  ROLE_ADMIN,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  ROLE_SUPER_ADMIN
} from '../../helpers/constants';
import {
  addDocHubFormFieldsToForm,
  convertKeyValueToFormObject,
  getObjectForPrefill,
  setupContactOwnerDropdown
} from '../../helpers/formHelpers';
import { processTasksForSubmission } from '../../helpers/taskHelpers';
import AlertModal from '../AlertModal';
import Loader from '../Loader';
import NotFound from '../NotFound';
import FileViewer from '../common/FileViewer';
import File from '../common/file';
import InventoryPicker from '../inventory/InventoryPicker';
import PotentialUnitsOverview from '../inventory/PotentialUnitsOverview';
import Tasks from '../tasks/Tasks';
import { addChoicesToCRMForm, editFormJson, validateAndCleanupCRMForm } from './form';
import { cloneDeep } from 'lodash';
import { getProfileFromLocalStorage } from '../../helpers/session';
import { userHasAnyRole } from '../../helpers/roleHelpers';
import HomeShortlists from './HomeShortlists';

const EditCrm = ({
  contactFromSearch,
  appChoices,
  fromSearch,
  onContactEdit,
  onContactDelete,
  onSearchCRMFileLoaded,
  onAddInventoryPopupMetaChange,
  addInvenroryPopupMeta,
  onAddInvetoryPopupMetaClose
}) => {
  const { id: idFromParams } = useParams();

  const [form, setForm] = useState(null);
  const [id] = useState(contactFromSearch ? contactFromSearch._id : idFromParams);
  const [isLoading, setLoading] = useState(!fromSearch);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [crmError, setCrmError] = useState('');
  const [formDisabled, setFormDisabled] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [crmDeleting, setCrmDeleting] = useState(false);
  const [contact, setContact] = useState();
  const [selectedInventory, setSelectedInventory] = useState([]);
  const [inventoryPopupMeta, setInventoryPopupMeta] = useState(null);
  const [zipCodes, setZipCodes] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [showHomeShortlists, setShowHomeShortlists] = useState(false);
  const [deleteFileModalMeta, setDeleteFileModalMeta] = useState(null);
  const [tasks, setTasks] = useState([]);
  const tasksValidationRef = useRef(null);
  const [currentUserId] = useState(() => {
    const profile = getProfileFromLocalStorage();
    return profile ? profile._id : null;
  });

  const history = useHistory();

  const fetchContact = async () => {
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.CONTACTS_SEARCH,
      requestBody: {
        filter: {
          _id: id
        }
      }
    });

    setLoading(false);
    if (error) return setCrmError(error);

    if (response.length === 0) {
      return setCrmError('No contract found!');
    }

    const contact = response[0];
    if (!fromSearch && contact['exportedContract']) {
      return history.push(`/contract/${contact['exportedContract']._id}`);
    }

    updateContactState(contact);
  };

  useEffect(() => {
    if (!fromSearch) {
      fetchContact();
    } else {
      updateContactState(contactFromSearch, !contactFromSearch['fileNames']);
    }
  }, []);

  const setUpForm = initialContact => {
    const form = cloneDeep(editFormJson());
    const newFormJson = addDocHubFormFieldsToForm({ formJson: form, appChoices, model: 'Contact' });
    addChoicesToCRMForm({ appChoices, form: newFormJson, initialFormValues: initialContact });
    setZipCodes(appChoices.find(a => a.key === 'zipCodes')['values']);

    setupContactOwnerDropdown({ appChoices, formJson: newFormJson });

    setForm(newFormJson);
  };

  const updateContactState = contact => {
    const clonedContact = cloneDeep(contact);
    setSelectedInventory(clonedContact['inventoryUnit'] || []);
    setTasks(contact.tasks || []);

    convertKeyValueToFormObject(clonedContact, appChoices);
    setContact(clonedContact);
    setUpForm(contact);
    setLoading(false);
  };

  const onCrmEditFormSubmit = async form => {
    if (uploadedFiles.some(f => f.status === 'ERROR')) {
      return toast.error('One or more files have failed uploading, please discard these files and try again!');
    }

    if (uploadedFiles.some(f => f.status !== 'UPLOADED')) {
      return toast.error('Some of the files are still uploading to the server, please try again in a moment!');
    }

    //checks
    const valid = validateAndCleanupCRMForm(form, contact?.additionalFields, contact?.customEntities);
    if (!valid) return;

    if (tasksValidationRef.current && !tasksValidationRef.current()) return;

    form['files'] = [...contact.files.map(f => f._id), ...uploadedFiles.map(f => f.id)];
    form['inventoryUnit'] = selectedInventory ? selectedInventory.map(i => i['_id']) : [];
    form['tasks'] = processTasksForSubmission(tasks, currentUserId, true, false);

    setButtonsDisabled(true);
    toast.info('Please wait, processing contact..');

    const { response, error } = await makeApiRequests({
      requestBody: form,
      endpoint: ENDPOINTS.CONTACTS_WITH_ID(contact._id),
      method: 'PUT'
    });

    setButtonsDisabled(false);
    if (error) return toast.error(error);

    toast.success(`Crm updated successfully!`);
    setUploadedFiles([]);

    const details = {
      ...response
      //TODO manage files
    };

    if (onContactEdit) {
      setFormDisabled(true);
      onContactEdit && onContactEdit(details);
    } else {
      setLoading(true);
      setTimeout(() => setLoading(false), 100);
    }
    updateContactState(details);
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
  };

  const deleteCrm = async () => {
    setCrmDeleting(true);

    const { response, error } = await makeApiRequests({
      method: 'DELETE',
      endpoint: ENDPOINTS.CONTACTS_WITH_ID(contact._id)
    });

    setCrmDeleting(false);

    if (error) {
      return toast.error(error);
    }

    setShowDeleteModal(false);
    toast(`Contact deleted successfully!`, {
      type: 'success'
    });

    if (fromSearch) {
      setShowDeleteModal(false);
      onContactDelete && onContactDelete(contact._id);
    } else {
      history.push('/pipeline/crm');
    }
  };

  const autoFillAddress = e => {
    try {
      const zipCode = e ? e.target.value : document.getElementById('zipCode').value;
      const zipCodeFiltered = zipCodes.filter(zip => zipCode == zip['Zip Code']);

      if (zipCodeFiltered.length > 0) {
        document.getElementById('propertyState').value = zipCodeFiltered[0]['Property State'];
        document.getElementById('propertyCity').value = zipCodeFiltered[0]['Property City'];
        document.getElementById('propertyCounty').value = zipCodeFiltered[0]['Property County'];
      }
    } catch (e) {}
  };

  const autoFillMailingAddress = e => {
    try {
      const zipCode = e ? e.target.value : document.getElementById('mailingZip').value;
      const zipCodeFiltered = zipCodes.filter(zip => zipCode == zip['Zip Code']);

      if (zipCodeFiltered.length > 0) {
        document.getElementById('mailingState').value = zipCodeFiltered[0]['Property State'];
        document.getElementById('mailingCity').value = zipCodeFiltered[0]['Property City'];
        document.getElementById('mailingCounty').value = zipCodeFiltered[0]['Property County'];
      }
    } catch (e) {}
  };

  window['autoFillMailingAddress'] = autoFillMailingAddress;
  window['autoFillAddress'] = autoFillAddress;
  window['onCrmEditFormSubmit'] = onCrmEditFormSubmit;

  if (crmError) {
    return <NotFound text={crmError} />;
  }

  const onButtonClick = () => {
    document.getElementById('update').click();
  };

  const ButtonsRow = ({ bottom }) => (
    <Row>
      <Col xs={12} className={`text-right ${bottom ? 'my-4' : ' mb-3 mt-2'}`}>
        <Button disabled={buttonsDisabled} onClick={() => onButtonClick(false)} size="sm" className="ml-2">
          Save Record
        </Button>
      </Col>
    </Row>
  );

  const onLinkUnitClick = replacingUnit => {
    setInventoryPopupMeta({ replacingUnit });
  };

  const onRemoveUnitClick = removingUnit => {
    const existingIndex = selectedInventory.findIndex(i => i['_id'] === removingUnit['_id']);
    if (existingIndex !== -1) {
      selectedInventory.splice(existingIndex, 1);
    }
    setSelectedInventory([...selectedInventory]);
  };

  const onInventorySubmit = inventoryList => {
    const inventory = inventoryList[0];
    const { replacingUnit } = inventoryPopupMeta;

    if (replacingUnit) {
      const existingIndex = selectedInventory.findIndex(i => i['_id'] === replacingUnit['_id']);
      if (existingIndex !== -1) {
        selectedInventory[existingIndex] = inventory;
      }
      setSelectedInventory([...selectedInventory]);
    } else {
      setSelectedInventory([...selectedInventory, inventory]);
    }
    setInventoryPopupMeta(null);
  };

  const onUnitSelect = unit => {
    setInventoryPopupMeta({ ...inventoryPopupMeta, selectedUnit: unit });
  };

  const onFileDeleteClick = file => {
    setDeleteFileModalMeta({ file });
  };

  const deleteFile = async () => {
    const { file: toBeDeletedFile } = deleteFileModalMeta;
    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: true });

    const newFileArray = contact.files.filter(f => f._id !== toBeDeletedFile._id).map(f => f._id);

    const { response, error } = await makeApiRequests({
      requestBody: { files: newFileArray },
      endpoint: ENDPOINTS.CONTACTS_WITH_ID(contact._id),
      method: 'PUT'
    });

    setDeleteFileModalMeta({ ...deleteFileModalMeta, deletingFile: false });
    if (error) {
      return toast.error(error);
    }

    setDeleteFileModalMeta(null);
    updateContactState(response);
    onContactEdit && onContactEdit(response, true);
    deleteFileFromServer(toBeDeletedFile);
  };

  return (
    <>
      {isLoading && <Loader />}
      {!isLoading && form && contact && (
        <>
          <Row>
            <Col className="text-md-right mt-2 mt-md-0">
              {userHasAnyRole(ROLE_SUPER_ADMIN, ROLE_ADMIN, ROLE_SALES, ROLE_SALES_MANAGER, ROLE_SALES_AND_INVENTORY_MANAGER) && <>
                <Button size="sm" variant="outline-dark" className="mr-1" onClick={() => setShowHomeShortlists(true)}>Send three homes</Button>
                <HomeShortlists contactId={contact._id} show={showHomeShortlists} onHide={() => setShowHomeShortlists(false)} />
              </>}
              {userHasAnyRole(
                ROLE_SUPER_ADMIN,
                ROLE_ADMIN,
                ROLE_SALES,
                ROLE_SALES_MANAGER,
                ROLE_SALES_AND_INVENTORY_MANAGER
              ) && (
                <Link target="_blank" to={`/dochub/print-groups?contactId=${contact['_id']}&source=Contact`}>
                  <Button
                    onClick={e => {
                      e.stopPropagation();
                    }}
                    className="mr-1"
                    variant="outline-dark"
                    size="sm"
                  >
                    <Printer className="align-text-top" />
                  </Button>
                </Link>
              )}
              {userHasAnyRole(
                ROLE_SUPER_ADMIN,
                ROLE_ADMIN,
                ROLE_SALES,
                ROLE_SALES_MANAGER,
                ROLE_SALES_AND_INVENTORY_MANAGER
              ) && (
                <Link
                  to={`/conversation-bank?contactId=${contact._id}&returnTo=${encodeURIComponent(`/crm/${contact._id}`)}`}
                >
                  <Button
                    onClick={e => e.stopPropagation()}
                    className="mr-1"
                    variant="outline-dark"
                    size="sm"
                  >
                    Conversation Bank
                  </Button>
                </Link>
              )}

              <Button onClick={() => setShowFileViewer(true)} variant="outline-dark" size="sm" className="py-1 ml-2">
                <Files className="align-text-top mr-2" />
                View Files
              </Button>

              {!contact['exportedContract'] && (
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => setFormDisabled(false)}
                  className="py-1 ml-2"
                  disabled={!formDisabled || buttonsDisabled}
                >
                  <Pen className="align-text-top mr-2" />
                  {formDisabled ? 'Edit' : 'Edit Mode Active'}
                </Button>
              )}
              <Button
                size="sm"
                variant="outline-danger"
                onClick={() => setShowDeleteModal(true)}
                className="py-1 ml-2"
                disabled={crmDeleting || buttonsDisabled}
              >
                <Trash className="align-text-top mr-2" />
                Delete
              </Button>
              <AlertModal
                alertText="Are you sure you want to delete the contact? This action cannot be undone!"
                show={showDeleteModal}
                onHide={handleDeleteModalClose}
                continueButtonText="Delete"
                continueButtonVariant="danger"
                onContinueClick={deleteCrm}
                progressText={'Deleting contact...'}
                showProgress={crmDeleting}
                onDismissClick={handleDeleteModalClose}
              />
            </Col>
          </Row>
          <hr />
          {!formDisabled && <ButtonsRow />}

          <FormGenerator
            formJson={form}
            formValues={{ update: getObjectForPrefill(contact) }}
            formDisabled={formDisabled}
          />
          {!formDisabled && (
            <File uploadedFiles={uploadedFiles} onUploadedFilesChange={setUploadedFiles} containerClassName="mx-3" />
          )}
          <div className="m-3">
            <PotentialUnitsOverview
              disabled={formDisabled}
              onLinkNewUnitClick={onLinkUnitClick}
              onRemoveUnitClick={onRemoveUnitClick}
              onChangeUnitClick={onLinkUnitClick}
              inventoryUnits={selectedInventory}
            />
            <InventoryPicker
              excludedUnitIds={selectedInventory ? selectedInventory?.map(u => u?.['_id']) : []}
              show={inventoryPopupMeta !== null}
              onSubmit={onInventorySubmit}
              selectedUnits={
                inventoryPopupMeta && inventoryPopupMeta.selectedUnit ? [inventoryPopupMeta.selectedUnit] : []
              }
              onUnitSelect={onUnitSelect}
              onInventoryPickerClose={() => setInventoryPopupMeta(null)}
              onAddInventoryPopupMetaChange={onAddInventoryPopupMetaChange}
              addInvenroryPopupMeta={addInvenroryPopupMeta}
              onAddInvetoryPopupMetaClose={onAddInvetoryPopupMetaClose}
            />
          </div>
          {contact['exportedContract'] && (
            <h6 className="midFont text-right text-muted  mt-3 mb-0">
              <i>
                This contact has already been exported to contract{' '}
                <Link target="_blank" to={`/contract/${contact['exportedContract']._id}`}>
                  <b className="text-dark">{contact['exportedContract']['buyer']}</b>
                </Link>
                . You are not allowed to edit it.
              </i>
            </h6>
          )}
          <div className="m-3">
            <Tasks
              tasks={tasks}
              disabled={formDisabled}
              onTasksChange={setTasks}
              appChoices={appChoices}
              currentUserId={currentUserId}
              onValidationRef={tasksValidationRef}
            />
          </div>
          {!formDisabled && <ButtonsRow bottom />}
          <FileViewer
            sidebarZIndex={2000}
            show={showFileViewer}
            onHide={() => setShowFileViewer(false)}
            files={contact['files']}
            driveFolder={contact['driveFolder']}
            onFileDeleteClick={onFileDeleteClick}
          />
          <AlertModal
            show={deleteFileModalMeta !== null}
            alertText={`Are you sure to delete this file (${
              deleteFileModalMeta
                ? deleteFileModalMeta.file.metadata && deleteFileModalMeta.file.metadata.originalName
                  ? deleteFileModalMeta.file.metadata.originalName
                  : deleteFileModalMeta.file.filename
                : ''
            }). You will lose access to this file permanently?`}
            showProgress={deleteFileModalMeta && deleteFileModalMeta.deletingFile}
            progressText="Deleting file..."
            continueButtonText="Delete"
            onDismissClick={() => setDeleteFileModalMeta(null)}
            onHide={() => setDeleteFileModalMeta(null)}
            onContinueClick={deleteFile}
          />
        </>
      )}
    </>
  );
};

export default EditCrm;
